import React, { useState, useEffect } from 'react';
import PolicyDocuments from '../components/PolicyDocuments';
import './Pages.css';

const PendingCancellations = ({ userRole }) => {
  const [cancellations, setCancellations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCancellation, setSelectedCancellation] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [approving, setApproving] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const CONFIRM_PHRASE = 'cancel policy';

  useEffect(() => {
    fetchPendingCancellations();
  }, []);

  const fetchPendingCancellations = async () => {
    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/cancellations/pending');
      const data = await response.json();

      if (data.success) {
        setCancellations(data.data);
      } else {
        setError('Failed to load pending cancellations');
      }
    } catch (err) {
      console.error('Error fetching cancellations:', err);
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleViewCancellation = (cancellation) => {
    setSelectedCancellation(cancellation);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedCancellation(null);
    setConfirmText('');
  };

  const handleApprove = async () => {
    if (!selectedCancellation) return;

    if (confirmText.trim().toLowerCase() !== CONFIRM_PHRASE) {
      return;
    }

    setApproving(true);

    try {
      const userEmail = localStorage.getItem('userEmail');

      const response = await fetch(`https://main.victriedemos.com:3017/api/cancellations/${selectedCancellation.Id}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          approvedBy: userEmail
        }),
      });

      const data = await response.json();

      if (data.success) {
        const definiteMessage = data.definiteCancellation
          ? `\n\nDefinite Assurance: ${data.definiteCancellation.success ? '✓' : '✗'} ${data.definiteCancellation.message}`
          : '';
        alert(`✅ Cancellation approved successfully!${definiteMessage}`);
        handleCloseModal();
        fetchPendingCancellations(); // Refresh the list
      } else {
        alert('❌ Failed to approve cancellation: ' + data.message);
      }
    } catch (error) {
      console.error('Error approving cancellation:', error);
      alert('❌ An error occurred while approving cancellation.');
    } finally {
      setApproving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading pending cancellations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Pending Cancellations</h1>
        <span className="badge-count">{cancellations.length} Pending</span>
      </div>

      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      <div className="table-container">
        {cancellations.length === 0 ? (
          <div className="empty-state">
            <p>No pending cancellations</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Policy Number</th>
                <th>Client Name</th>
                <th>Registration</th>
                <th>Cancellation Reason</th>
                <th>Requested By</th>
                <th>Date Requested</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {cancellations.map((cancellation, index) => (
                <tr key={cancellation.Id}>
                  <td>{index + 1}</td>
                  <td className="policy-number">{cancellation.PolicyNumber || 'N/A'}</td>
                  <td className="client-name">{cancellation.ClientName || 'N/A'}</td>
                  <td>{cancellation.Registration || 'N/A'}</td>
                  <td className="cancellation-reason">
                    {cancellation.CancellationReason?.substring(0, 50)}
                    {cancellation.CancellationReason?.length > 50 ? '...' : ''}
                  </td>
                  <td>{cancellation.RequestedByName || cancellation.CancelledBy || 'N/A'}</td>
                  <td>{formatDate(cancellation.DateOfCancellation)}</td>
                  <td>
                    <button 
                      className="action-icon-btn" 
                      title="View Details"
                      onClick={() => handleViewCancellation(cancellation)}
                    >
                      👁️
                    </button>
                    {userRole === 1 && (
                      <button
                        className="action-icon-btn approve-icon"
                        title="Approve"
                        onClick={() => handleViewCancellation(cancellation)}
                      >
                        ✅
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* View Cancellation Modal */}
      {showModal && selectedCancellation && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📋 Cancellation Request Details</h2>
              <button className="close-modal-btn" onClick={handleCloseModal}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              {/* Policy Information */}
              <div className="detail-section">
                <h3>Policy Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Registration:</label>
                    <span>{selectedCancellation.Registration || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Client Name:</label>
                    <span>{selectedCancellation.ClientName || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Client Email:</label>
                    <span>{selectedCancellation.ClientEmail || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Cancellation Details */}
              <div className="detail-section">
                <h3>Cancellation Details</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Cancellation Reason:</label>
                    <span className="highlight-change">{selectedCancellation.CancellationReason || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Request Info */}
              <div className="detail-section">
                <h3>Request Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Requested By:</label>
                    <span>{selectedCancellation.RequestedByName || selectedCancellation.CancelledBy || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Date Requested:</label>
                    <span>{formatDate(selectedCancellation.DateOfCancellation)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Status:</label>
                    <span className="status-badge pending">Pending Approval</span>
                  </div>
                </div>
              </div>

              <PolicyDocuments policy={selectedCancellation} />

              {/* Action Buttons */}
              {userRole === 1 && (
                <>
                  <div className="error-banner">
                    ⚠️ <strong>This action cannot be reversed.</strong> Once approved, the policy will be marked Cancelled permanently.
                  </div>

                  <div className="form-group">
                    <label>Type <strong>"{CONFIRM_PHRASE}"</strong> below to confirm *</label>
                    <input
                      type="text"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      className="form-input"
                      placeholder={CONFIRM_PHRASE}
                      disabled={approving}
                      autoComplete="off"
                    />
                  </div>

                  <div className="modal-actions">
                    <button
                      className="action-button cancel-endorsement-btn"
                      onClick={handleCloseModal}
                      disabled={approving}
                    >
                      Close
                    </button>
                    <button
                      className="action-button submit-cancellation-btn"
                      onClick={handleApprove}
                      disabled={approving || confirmText.trim().toLowerCase() !== CONFIRM_PHRASE}
                    >
                      {approving ? 'Approving...' : '✓ Approve Cancellation'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingCancellations;
